import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import {
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import { convertPxToRem } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface ConsultationTypeSelectionProps {
  onDirectToDoctorConsultClick: () => void;
  onNurseConsultClick: () => void;
  onSeeAllConditionsClick: () => void;
  memberProfile: MemberProfile;
}

export function ConsultationTypeSelection({
  onDirectToDoctorConsultClick,
  onNurseConsultClick,
  onSeeAllConditionsClick,
  memberProfile,
}: ConsultationTypeSelectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isEnableSymptomCheckCopyRemoval = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_SYMPTOM_CHECK_COPY_REMOVAL
  );

  const directToDoctorPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  const nurseAssistedPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
    );

  function onBackButtonClick() {
    navigate(-1);
  }

  function onDirectToDoctorConsultationTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | Doctor on demand",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onDirectToDoctorConsultClick();
  }

  function onNurseConsultationTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | Nurse consult",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onNurseConsultClick();
  }

  function onSeeAllConditionsTypeClick() {
    trackUserInteraction({
      linkText: "ConsultationTypeSelection | See all conditions",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onSeeAllConditionsClick();
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: "Medical assistance:Consultation type selection",
      pageSubSection1: "Medical assistance",
      pageSubSection2: "Medical assistance:Consultation type selection",
      pageCategory: "Medical assistance",
    });
  }, []);

  function replaceCentsFromAmount(amountFormatted: string) {
    return amountFormatted.replace(".00", "");
  }

  return (
    <Stack
      color="neutral.dark"
      sx={{
        ...defaultFullscreenPageStyling,
        minHeight: "100%",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Stack p={2} spacing={2}>
        <Stack>
          <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
        </Stack>
        <Stack>
          <Typography variant="h1" color="inherit">
            {t("ConsultationTypeSelection.title")}
          </Typography>

          <Stack spacing={4} py={4}>
            <Stack
              component="button"
              sx={{
                ...panelStyle,
                cursor: "pointer",
                border: "none",
              }}
              aria-label={t("ConsultationTypeSelectionV2.DirectToDoctor.title")}
              color="neutral"
              onClick={onDirectToDoctorConsultationTypeClick}
            >
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Stack spacing={1}>
                  <Stack
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    gap={1}
                    alignItems="left"
                  >
                    <Typography variant="h3" fontWeight={600} textAlign="left">
                      {t("ConsultationTypeSelectionV2.DirectToDoctor.title")}
                    </Typography>
                    <Stack
                      sx={{
                        backgroundColor: "accent1.main",
                        borderRadius: 2,
                        alignSelf: "start",
                      }}
                      padding={1}
                    >
                      <Typography
                        fontWeight={600}
                        variant="body2"
                        color="primary.contrastText"
                      >
                        {t("common.mostPopular")}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      color="neutral.light"
                      textAlign="left"
                    >
                      {t("ConsultationTypeSelectionV2.DirectToDoctor.subtitle")}
                    </Typography>
                    <IconLoader
                      sx={{ fontSize: 30 }}
                      color="primary"
                      icon="ChevronNextIcon"
                    />
                  </Stack>
                </Stack>
                {directToDoctorPaymentRates?.isDiscountApplied ? (
                  <Box
                    textAlign="left"
                    sx={{
                      background: (theme) => theme.palette.accent4.main,
                      width: convertPxToRem(130),
                      borderRadius: 2,
                      paddingRight: convertPxToRem(30),
                      paddingLeft: convertPxToRem(20),
                      paddingTop: convertPxToRem(5),
                      paddingBottom: convertPxToRem(5),
                    }}
                  >
                    <Typography color="primary.contrastText" fontWeight={600}>
                      {t("ConsultationTypeSelection.discount.amount", {
                        amountFormatted: replaceCentsFromAmount(
                          directToDoctorPaymentRates?.amountFormatted || ""
                        ),
                      })}
                    </Typography>
                    <Typography
                      color="primary.contrastText"
                      fontWeight={600}
                      variant="body2"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {t("ConsultationTypeSelection.discount.original.amount", {
                        amountFormatted: replaceCentsFromAmount(
                          directToDoctorPaymentRates?.originalAmountFormatted ||
                            ""
                        ),
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    textAlign="center"
                    sx={{
                      background: (theme) => theme.palette.primary.main,
                      width: 90,
                      borderRadius: 2,
                      padding: 1,
                    }}
                  >
                    <Typography color="primary.contrastText" fontWeight={600}>
                      {replaceCentsFromAmount(
                        directToDoctorPaymentRates?.amountFormatted || ""
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>

            <Stack
              component="button"
              sx={{
                ...panelStyle,
                cursor: "pointer",
                border: "none",
              }}
              aria-label={t("ConsultationTypeSelectionV2.nurseConsult.title")}
              color="neutral"
              onClick={onNurseConsultationTypeClick}
            >
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Stack spacing={1}>
                  <Stack
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    gap={1}
                    alignItems="left"
                  >
                    <Typography variant="h3" fontWeight={600} textAlign="left">
                      {t("ConsultationTypeSelectionV2.nurseConsult.title")}
                    </Typography>
                  </Stack>

                  <Stack flexDirection="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      color="neutral.light"
                      textAlign="left"
                    >
                      {isEnableSymptomCheckCopyRemoval ? (
                        <>
                          {t(
                            "ConsultationTypeSelectionV2.nurseConsult.subtitle.v2"
                          )}
                        </>
                      ) : (
                        <>
                          {t(
                            "ConsultationTypeSelectionV2.nurseConsult.subtitle"
                          )}
                        </>
                      )}
                    </Typography>
                    <IconLoader
                      sx={{ fontSize: 30, alignSelf: "center" }}
                      color="primary"
                      icon="ChevronNextIcon"
                    />
                  </Stack>
                </Stack>
                {nurseAssistedPaymentRates?.isDiscountApplied ? (
                  <Box
                    textAlign="left"
                    sx={{
                      background: (theme) => theme.palette.accent4.main,
                      width: convertPxToRem(130),
                      borderRadius: 2,
                      paddingRight: convertPxToRem(30),
                      paddingLeft: convertPxToRem(20),
                      paddingTop: convertPxToRem(5),
                      paddingBottom: convertPxToRem(5),
                    }}
                  >
                    <Typography color="primary.contrastText" fontWeight={600}>
                      {t("ConsultationTypeSelection.discount.amount", {
                        amountFormatted: replaceCentsFromAmount(
                          nurseAssistedPaymentRates?.amountFormatted || ""
                        ),
                      })}
                    </Typography>
                    <Typography
                      color="primary.contrastText"
                      fontWeight={600}
                      variant="body2"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {t("ConsultationTypeSelection.discount.original.amount", {
                        amountFormatted: replaceCentsFromAmount(
                          nurseAssistedPaymentRates?.originalAmountFormatted ||
                            ""
                        ),
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    textAlign="center"
                    sx={{
                      background: (theme) => theme.palette.primary.main,
                      width: 90,
                      borderRadius: 2,
                      padding: 1,
                    }}
                  >
                    <Typography color="primary.contrastText" fontWeight={600}>
                      {replaceCentsFromAmount(
                        directToDoctorPaymentRates?.amountFormatted || ""
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>

            <Stack
              component="button"
              sx={{
                ...panelStyle,
                cursor: "pointer",
                border: "none",
              }}
              aria-label={t("ConsultationTypeSelectionV2.seeConditions.title")}
              color="neutral"
              onClick={onSeeAllConditionsTypeClick}
            >
              <Stack
                sx={{ width: "100%" }}
                spacing={2}
                flexDirection="row"
                justifyContent="space-between"
              >
                <Stack spacing={1}>
                  <Stack
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    gap={1}
                    alignItems="left"
                  >
                    <Typography variant="h3" fontWeight={600} textAlign="left">
                      {t("ConsultationTypeSelectionV2.seeConditions.title")}
                    </Typography>
                  </Stack>

                  <Stack flexDirection="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      color="neutral.light"
                      textAlign="left"
                    >
                      {t("ConsultationTypeSelectionV2.seeConditions.subtitle")}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack alignSelf="center">
                  <IconLoader
                    sx={{ fontSize: 30 }}
                    color="primary"
                    icon="ChevronNextIcon"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
